import { useI18n } from '@change-corgi/core-react/i18n';
import { useKnownAvailableParams, useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { paymentMethod } from '#src/app/shared/utils/payments';
import type { PaymentType } from '#src/app/shared/utils/payments';
import { calculateImpressions } from '#src/app/shared/utils/promote';

import { usePromoteFormData } from '../../../../../formContext/formData';
import { usePromoteFormState } from '../../../../../formContext/formState';
import { useSaveBraintreePaymentTemporarily } from '../../../useSaveBraintreePaymentTemporarily';
import { useSetReceiptCookie } from '../../../useSetReceiptCookie';
import { useUserInfo } from '../../../useUserInfo';

import {
	StorePromoteOrShareMembershipUpsellContext,
	type StorePromoteOrShareMembershipUpsellContextMutation,
	type StorePromoteOrShareMembershipUpsellContextMutationVariables,
} from './storeMembershipUpsellContext.graphql';

type Props = {
	token: string | null | undefined;
	accountId: string | null | undefined;
	paymentType: PaymentType;
	tokenType: 'permanent' | 'temporary';
	membershipAmount: number;
};

// PSF has no revenue_ppmu_day_zero experiment, so the standard 30-day trial always applies.
const TRIAL_PERIOD_DAYS = 30;

export function useNavigateToPpmuPage(): (props: Props) => Promise<boolean> {
	const navigate = useNavigate();
	const { gql } = useUtilityContext();
	const { amountToBaseUnits } = useI18n();
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const setReceiptCookie = useSetReceiptCookie();
	const getUserInfo = useUserInfo();
	const btSavedTemporarily = useSaveBraintreePaymentTemporarily();

	const [{ amount }] = usePromoteFormState();
	const {
		currency,
		petition: { id: petitionId },
		promotionSettings: { cpm, bonusCpm, bonusThreshold },
	} = usePromoteFormData();
	const track = useTracking();

	return async function navigateToPpmuPage({
		token,
		accountId,
		paymentType,
		tokenType,
		membershipAmount,
	}: Props): Promise<boolean> {
		const { email, firstName, lastName } = getUserInfo(paymentType);
		const { gateway, type: paymentTypeGQL } = paymentMethod(paymentType);
		const promotionAmountCents = amountToBaseUnits(amount, currency.code);
		const impressions = calculateImpressions(promotionAmountCents, cpm, bonusCpm, bonusThreshold);
		const postDeclineDestination = `/p/${encodeURIComponent(slug)}/promote/thank_you?amount=${String(amount)}&impressions=${String(impressions)}`;

		try {
			const { data } = await gql.fetch<
				StorePromoteOrShareMembershipUpsellContextMutation,
				StorePromoteOrShareMembershipUpsellContextMutationVariables
			>({
				query: StorePromoteOrShareMembershipUpsellContext,
				variables: {
					input: {
						token,
						tokenType: tokenType === 'permanent' ? 'PERMANENT' : 'TEMPORARY',
						accountId,
						gateway,
						paymentType: paymentTypeGQL,
						// usingSavedPaymentMethod is always false in PSF (not supported)
						usingSavedPaymentMethod: false,
						btSavedTemporarily,
						postDeclineDestination,
						email,
						firstName,
						lastName,
						source: 'POST_PROMOTE_UPSELL_DECOUPLED',
						interval: 'MONTH',
						trialPeriodDays: TRIAL_PERIOD_DAYS,
						petitionId,
					},
				},
				path: '/payments/storeMembershipUpsellContext',
				rejectOnError: true,
			});
			const result = data.storeMembershipUpsellContext;
			if (result.__typename !== 'StoreMembershipUpsellContextSuccess') return false;
			setReceiptCookie(membershipAmount);
			navigate(
				`/p/${encodeURIComponent(slug)}/member/offer?ref=${encodeURIComponent(result.membershipUpsellContextId)}&amount=${String(amountToBaseUnits(membershipAmount, currency.code))}`,
				{
					forceMode: 'internal',
				},
			);
			return true;
		} catch (error) {
			// transport/server failure — skip the upsell (returning false lands the user on the
			// promote thank-you page); promotion charge already succeeded, so don't surface as payment error
			void track('pp_promote_upsell_context_store_error');
			return false;
		}
	};
}
